import { get, isEmpty, tail } from 'lodash-es'
import { useContext, useEffect, useMemo, useRef } from 'react'
import { Feature } from 'geojson'
import { LuBolt, LuEye, LuTriangleAlert } from 'react-icons/lu'

import { getPointCoordinates } from '@/utils/geoFenceConverters'
import {
  ACTIVE_GEOFENCE_LINESTRING_COLOR,
  ACTIVE_MARKER,
  GEO_FEATURE_MARKER_COLOR,
  HIGH_RISK_MARKER_COLOR,
  INACTIVE_GEOFENCE,
  ObservatoryContext,
  usePersistentObservatoryPageStore,
} from '@/feature/observatory'
import { hexToRgba } from '@/feature/observatory/utils/hexToRgba'
import {
  resolveLinestringColor,
  useRgbCycleShapes,
} from '@/feature/observatory/utils/useRgbCycleColor'
import { GeoFence } from '@/feature/observatory/types/GeoFence.types'
import { useDeferredLineStringBuffer } from '@/feature/observatory/utils/lineStringBuffer'
import { MapContext } from '@/feature/map/context/map'
import { calculateZoomForBounds, MapMarker, MapShape, pxToLatLng } from '@/feature/map'

import { MarkerBadge } from '@/components/MarkerBadge'

import styles from './GeoFenceMarker.module.css'

export interface GeoFenceMarkerProps {
  geoFence: GeoFence
}

export const GeoFenceMarker = ({ geoFence }: GeoFenceMarkerProps) => {
  const map = useContext(MapContext)
  const { geoFenceEvents } = useContext(ObservatoryContext)
  const selectedItem = usePersistentObservatoryPageStore(({ selectedItem }) => selectedItem)
  const setSelectedItem = usePersistentObservatoryPageStore(
    ({ setSelectedItem }) => setSelectedItem,
  )
  const asides = usePersistentObservatoryPageStore(({ asides }) => asides)
  const setGeoFenceModalId = usePersistentObservatoryPageStore(
    ({ setGeoFenceModalId }) => setGeoFenceModalId,
  )

  const geometryType = get(geoFence, 'geoJson.geometry.type')
  const isPolygon = geometryType === 'Polygon'
  const isLineString = geometryType === 'LineString'

  const longAddress = geoFence?.address?.split(',') ?? []
  longAddress.pop()
  const address = longAddress.join(',')

  // Prepare coordinates for polygon and linestring
  const polygonRing = get(geoFence, 'geoJson.geometry.coordinates[0]', [] as number[][])
  const geoPolies = useMemo(() => {
    return isPolygon
      ? tail(polygonRing).map((p: number[]) => ({
          lat: p[1],
          lng: p[0],
        }))
      : []
  }, [polygonRing, isPolygon])

  const lineCoords = useMemo(() => {
    const coords = get(geoFence, 'geoJson.geometry.coordinates', [] as number[][])
    const result = isLineString
      ? (coords as number[][]).map((p: number[]) => ({ lat: p[1], lng: p[0] }))
      : []
    return result
  }, [geoFence, isLineString])

  // Determine a sensible marker position
  const homePosition = get(geoFence, 'geoJson.properties.home') as
    | google.maps.LatLngLiteral
    | undefined
  const pointPosition = getPointCoordinates(geoFence.geoJson)
  const midLinePosition =
    isLineString && lineCoords.length ? lineCoords[Math.floor(lineCoords.length / 2)] : undefined
  const position = (homePosition || pointPosition || midLinePosition) as
    | google.maps.LatLngLiteral
    | undefined

  const events = geoFenceEvents[geoFence.id]

  const marker = useRef<google.maps.marker.AdvancedMarkerElement>(null)
  const shapeRef = useRef<google.maps.Circle | google.maps.Polygon | google.maps.Polyline>(null)
  const linestringPolylineRef = useRef<google.maps.Polyline>(null)
  const linestringBufferRef = useRef<google.maps.Polygon>(null)

  const tooltipRef = useRef<HTMLDivElement>(null)

  const isActiveGeoFence = selectedItem?.type === 'geoFence' && selectedItem?.id === geoFence.id

  useEffect(() => {
    const myMarker = marker.current
    const shapeRefCurrent = shapeRef?.current

    if (isActiveGeoFence && map && myMarker && shapeRefCurrent) {
      const panTo = () =>
        setTimeout(() => {
          let shapeBounds: google.maps.LatLngBounds | null = null
          if (shapeRefCurrent instanceof window.google.maps.Circle) {
            shapeBounds = shapeRefCurrent.getBounds()
          } else if (shapeRefCurrent instanceof window.google.maps.Polygon) {
            const bounds = new window.google.maps.LatLngBounds()
            geoPolies.forEach((path) => bounds.extend(path))
            shapeBounds = bounds
          } else if (shapeRefCurrent instanceof window.google.maps.Polyline) {
            const bounds = new window.google.maps.LatLngBounds()
            const path = shapeRefCurrent.getPath()?.getArray() ?? []
            path.forEach((p) => bounds.extend(p))
            shapeBounds = bounds
          }
          if (shapeBounds) {
            const targetZoom = calculateZoomForBounds(
              shapeBounds,
              map.map.getDiv().getBoundingClientRect(),
            )
            const position = shapeBounds.getCenter().toJSON()

            const tooltipElem = tooltipRef.current
            if (tooltipElem) {
              // Because of how the tooltip is setup, it doesn't expand the marker height, so they need
              // to be combined to get the correct centre point
              const { x, y, height } = tooltipElem.getBoundingClientRect()
              const { height: markerHeight } = tooltipElem.parentElement!.getBoundingClientRect()

              const topL = pxToLatLng(x, y, map.map, targetZoom)
              const botL = pxToLatLng(x, y + (height + markerHeight) / 2, map.map, targetZoom)
              if (topL && botL) {
                position.lat += topL.lat() - botL.lat()
              }
            }
            map.panTo({
              position,
              zoom: targetZoom,
            })
          }
        }, 0)
      panTo()
      return () => {}
    }
  }, [
    isActiveGeoFence,
    map,
    map?.panTo,
    geoFence,
    geoFence.id,
    geoFence.geoJson,
    marker.current,
    shapeRef,
    geoPolies,
  ])

  const circleOrPolygonParameters = isEmpty(geoPolies)
    ? {
        center: getPointCoordinates(geoFence.geoJson),
        radius: get(geoFence, 'geoJson.properties.radius', 0),
      }
    : {
        paths: geoPolies,
      }

  const linestringRawColor = isLineString
    ? (get(geoFence, 'geoJson.properties.color') as string | undefined)
    : undefined
  const linestringCustomColor = resolveLinestringColor(linestringRawColor, GEO_FEATURE_MARKER_COLOR)

  // Drive RGB cycling directly on Google Maps shapes — no React re-renders
  useRgbCycleShapes(linestringRawColor, linestringPolylineRef, linestringBufferRef)

  const polylineStrokeColor = geoFence.highRiskArea
    ? HIGH_RISK_MARKER_COLOR
    : isActiveGeoFence
      ? ACTIVE_GEOFENCE_LINESTRING_COLOR
      : linestringCustomColor

  const polylineParameters: Omit<google.maps.PolylineOptions, 'map'> | undefined = isLineString
    ? {
        path: lineCoords,
        strokeColor: polylineStrokeColor,
        strokeWeight: 3,
        zIndex: isActiveGeoFence ? 20000 : 10000,
        geodesic: true,
      }
    : undefined

  const hasCustomColor = !!linestringRawColor && linestringRawColor.toLowerCase() !== 'rgb'
  const bufferFillColor = geoFence.highRiskArea
    ? '#FF000040'
    : hasCustomColor
      ? hexToRgba(linestringCustomColor, 0.3)
      : '#1945B64D'

  // Build a simple buffered polygon (corridor) around the line using Google geometry
  // Deferred: polyline renders immediately, buffer computes async & hides below zoom 12
  const bufferMeters = get(geoFence, 'geoJson.properties.radius', 50)
  const bufferPolygon = useDeferredLineStringBuffer(lineCoords, bufferMeters, map?.map)

  return (
    <>
      {/* Buffer area */}
      {isLineString && bufferPolygon.length ? (
        <MapShape
          ref={linestringBufferRef}
          shape={window.google.maps.Polygon}
          map={map?.map}
          parameters={{
            map: map?.map,
            paths: [bufferPolygon],
            strokeWeight: 0,
            fillColor: bufferFillColor,
            fillOpacity: 1,
            zIndex: 9999,
          }}
        />
      ) : null}

      <MapShape
        ref={(instance: google.maps.Circle | google.maps.Polygon | google.maps.Polyline | null) => {
          shapeRef.current = instance
          if (isLineString && instance instanceof window.google.maps.Polyline) {
            linestringPolylineRef.current = instance
          }
        }}
        map={map?.map}
        shape={
          isLineString
            ? window.google.maps.Polyline
            : isEmpty(geoPolies)
              ? window.google.maps.Circle
              : window.google.maps.Polygon
        }
        parameters={
          isLineString
            ? (polylineParameters as Omit<google.maps.PolylineOptions, 'map'>)
            : {
                ...circleOrPolygonParameters,
                map: map?.map,
                options: {
                  strokeWeight: 0,
                  fillColor: geoFence.highRiskArea ? '#FF000040' : '#1945B64D',
                  fillOpacity: 1,
                  zIndex: 10000,
                },
              }
        }
      />
      <MapMarker
        ref={marker}
        zIndex={isActiveGeoFence ? ACTIVE_MARKER : INACTIVE_GEOFENCE}
        clusterer={false}
        onClick={() => {
          if (isActiveGeoFence) {
            setSelectedItem(undefined)
          } else {
            asides.setHideGeoFences(false)

            setSelectedItem({
              id: geoFence.id,
              type: 'geoFence',
            })

            setTimeout(() => {
              map?.panTo({
                position,
              })
            })
          }
        }}
        className={[
          styles.geoFenceMarker,
          isActiveGeoFence ? styles.geoFenceMarkerActive : undefined,
        ].join(' ')}
        position={position}
      >
        <div
          ref={tooltipRef}
          className={styles.markerTooltip}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className={styles.markerContainer}>
            <div className={styles.markerContent}>
              <div>
                {geoFence.highRiskArea && (
                  <div
                    className={[styles.markerContentDetailsLabel, styles.highRiskContainer].join(
                      ' ',
                    )}
                  >
                    <MarkerBadge glyph={LuTriangleAlert} size={23} color={HIGH_RISK_MARKER_COLOR} />
                    <span>High-Risk Area</span>
                  </div>
                )}
                <div className={styles.markerContentDetailsLabel}>
                  Title: <span className={styles.markerContentDetails}>{geoFence.title}</span>
                </div>
                <div className={styles.markerContentDetailsLabel}>
                  Address: <span className={styles.markerContentDetails}>{address}</span>
                </div>
                <div className={styles.markerContentDetailsLabel}>
                  Latitude: <span className={styles.markerContentDetails}>{position?.lat}</span>
                </div>
                <div className={styles.markerContentDetailsLabel}>
                  Longitude: <span className={styles.markerContentDetails}>{position?.lng}</span>
                </div>
                {/* Show radius only for circle geofences */}
                {!isPolygon && !isLineString ? (
                  <div className={styles.markerContentDetailsLabel}>
                    Radius:{' '}
                    <span className={styles.markerContentDetails}>
                      {(geoFence.geoJson as Feature).properties?.radius + 'm'}
                    </span>
                  </div>
                ) : null}
                <button
                  type={'button'}
                  onClick={() => setGeoFenceModalId(geoFence.id)}
                  className={styles.geoFenceContains}
                >
                  <span>
                    {/* TODO: remove part referencing events once fully switched to dedicated "aside" api */}
                    {geoFence.insideCount || (events?.inside?.length ?? 0)} Assets Inside
                  </span>
                  <LuEye size={12} strokeWidth={1.385} />
                </button>
              </div>
            </div>
          </div>
        </div>
        <div role="button" className={styles.marker}>
          {geoFence.highRiskArea ? (
            <MarkerBadge glyph={LuTriangleAlert} border size={40} color={HIGH_RISK_MARKER_COLOR} />
          ) : (
            <MarkerBadge glyph={LuBolt} size={40} border color={GEO_FEATURE_MARKER_COLOR} />
          )}
        </div>
      </MapMarker>
    </>
  )
}
